import React, { useState } from 'react';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { chargeTransactionsKey } from '@dashboard/lib/queryKeys';
import { useQuery } from '@tanstack/react-query';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import Spinner from '@dashboard/components/Spinner';
import Callout from '@dashboard/components/Callout';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import CardHeader from '@/dashboard/components/CardHeader';
import CardHeaderTitle from '@/dashboard/components/CardHeaderTitle';
import Badge from '@/dashboard/components/Badge';
import { format } from 'date-fns';
import { upperFirst } from 'lodash';
import { formatPrice } from '@/dashboard/services/formatting';
import Dialog from '@dashboard/components/Dialog';
import Button from '@dashboard/components/Button';
import { formatDateTime } from '@/dashboard/lib/format-date';
import getPaymentMethodLabel from '@/dashboard/services/getPaymentMethodLabel';
import getRefundReasonStatusLabel from '@/dashboard/services/status/getRefundReasonStatusLabel';
import Dash from '@/dashboard/components/Dash';

const PaymentsBookingsDetailTransactions = ({ payment }) => {
  const [open, setOpen] = useState(false);
  const [transaction, setTransaction] = useState(null);

  const { sellerId, token } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: chargeTransactionsKey(payment._id),
    queryFn: () => fetcher(`/sellers/${sellerId}/charges/${payment._id}/transactions`, token),
    enabled: !!payment._id,
  });

  const hasRefundedTransactions = data?.transactions?.some(
    transaction => transaction.status === 'refunded' || transaction.status === 'partially_refunded',
  );

  // The date of when the transactions were implemented - https://github.com/checkout-page/checkoutpage-api/pull/268
  const mergeDate = new Date('2025-06-19T02:34:18.000Z');
  const paymentCreatedBeforeTransactionFeature = new Date(payment.createdAt) < mergeDate;

  const getStatusAppearance = status => {
    switch (status) {
      case 'refunded':
      case 'partially_refunded':
      case 'paid':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'info';
    }
  };

  const getTransactionType = transaction => {
    switch (transaction.status) {
      case 'refunded':
        return 'Refund';
      case 'partially_refunded':
        return 'Partial refund';
      case 'partially_paid':
        return 'Partial payment';
      default:
        return 'Payment';
    }
  };

  return (
    <div className="flex flex-col lg:flex-row items-start justify-center">
      <div className="order-2 lg:order-1 w-full lg:pr-8 px-4">
        <Card className="mx-auto !px-0 !py-2">
          <CardHeader>
            <CardHeaderTitle>Transactions</CardHeaderTitle>
          </CardHeader>

          <CardContent>
            {isLoading && <Spinner />}

            {isError && <Callout variant="error">Failed to load transactions</Callout>}

            {data && data.transactions?.length === 0 && !isLoading && !isError && (
              <Callout variant="info">No transactions found for this booking.</Callout>
            )}

            {data && data.transactions?.length > 0 && (
              <div className="overflow-auto">
                <CardListItem variant="header" className="!mt-0">
                  <CardListItemElement size="fill" className="!pl-0">
                    Type
                  </CardListItemElement>
                  <CardListItemElement size="xl" align="right">
                    Payment gateway
                  </CardListItemElement>

                  <CardListItemElement size="medium">Amount</CardListItemElement>
                  <CardListItemElement size="2xl">Payment method</CardListItemElement>
                  <CardListItemElement size="xl">Created at</CardListItemElement>
                  <CardListItemElement size="medium">Status</CardListItemElement>
                  {hasRefundedTransactions && (
                    <CardListItemElement size="medium"></CardListItemElement>
                  )}
                </CardListItem>

                {data.transactions.map(transaction => (
                  <CardListItem key={transaction._id}>
                    <CardListItemElement size="fill" className="!pl-0">
                      {getTransactionType(transaction)}
                    </CardListItemElement>
                    <CardListItemElement size="xl">
                      {transaction.method && transaction.method?.gateway ? (
                        upperFirst(transaction.method?.gateway)
                      ) : (
                        <Dash />
                      )}
                    </CardListItemElement>

                    <CardListItemElement size="medium" align="right">
                      {formatPrice({ amount: transaction.amount, currency: transaction.currency })}
                    </CardListItemElement>
                    <CardListItemElement size="2xl">
                      {transaction.method ? (
                        getPaymentMethodLabel(transaction.method, { includeBrand: true })
                      ) : (
                        <Dash />
                      )}
                    </CardListItemElement>

                    <CardListItemElement size="xl">
                      {format(new Date(transaction.createdAt), 'd MMM, yyyy, HH:mm:ss')}
                    </CardListItemElement>

                    <CardListItemElement size="medium">
                      <Badge appearance={getStatusAppearance(transaction.status)}>
                        {transaction.status === 'refunded' ||
                        transaction.status === 'partially_refunded' ||
                        transaction.status === 'paid'
                          ? 'Succeeded'
                          : upperFirst(transaction.status.toLowerCase())}
                      </Badge>
                    </CardListItemElement>

                    {hasRefundedTransactions && (
                      <CardListItemElement size="medium">
                        {(transaction.status === 'refunded' ||
                          transaction.status === 'partially_refunded') && (
                          <Button
                            appearance="secondary"
                            size="small"
                            onClick={() => {
                              setOpen(true);
                              setTransaction(transaction);
                            }}
                          >
                            View reason
                          </Button>
                        )}
                      </CardListItemElement>
                    )}
                  </CardListItem>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {paymentCreatedBeforeTransactionFeature && !isLoading && (
          <div className="text-sm text-gray-500">
            Transaction history unavailable before {formatDateTime(mergeDate)}
          </div>
        )}

        <Dialog
          cancelButton={null}
          confirmButton={null}
          size="md"
          open={open}
          onCancel={() => {
            setOpen(false);
            setTransaction(null);
          }}
        >
          <div className="flex-row">
            <div className="font-bold">Refund reason</div>
            <div className="mb-4">{getRefundReasonStatusLabel(transaction?.reason)}</div>
            {transaction?.note && (
              <>
                <div className="font-bold">Note</div>
                <div>{transaction?.note}</div>
              </>
            )}
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default PaymentsBookingsDetailTransactions;
